import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { prefersReducedMotion, isMobileBreakpoint } from '@theme/utilities';
import { dragProgress, snapProgress, isDrag, revealClip } from '@theme/eme-card-drag-core';

const TIMING = { duration: 350, easing: 'ease' };
const ZOOMED = 'scale(1.04)';
const RESTING = 'scale(1)';

class EmeCardImageFade extends Component {
  #gallery = null;
  #card = null;

  #progress = 0;
  #start = null;
  #dragged = false;
  #reveal = null;
  #shownSlide = null;

  connectedCallback() {
    super.connectedCallback();
    this.#gallery = this.closest('.card-gallery');
    this.#shownSlide = this.#gallery?.querySelector('slideshow-slide[aria-hidden="false"]') ?? null;
    this.#card = this.closest('product-card');
    this.#card?.addEventListener(StandardEvents.productSelect, this.#handleVariantSelect);
    this.#card?.addEventListener('focusin', this.#handleFocusIn);
    this.#gallery?.addEventListener('slideshow:select', this.#handleSelect);
    this.#gallery?.addEventListener('pointerdown', this.#handleDown);
    this.#gallery?.addEventListener('pointermove', this.#handleMove);
    this.#gallery?.addEventListener('pointerup', this.#handleUp);
    this.#gallery?.addEventListener('pointercancel', this.#handleUp);
    this.#gallery?.addEventListener('dragstart', this.#handleDragStart);
    this.#gallery?.addEventListener('click', this.#handleClick, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#card?.removeEventListener(StandardEvents.productSelect, this.#handleVariantSelect);
    this.#card?.removeEventListener('focusin', this.#handleFocusIn);
    this.#gallery?.removeEventListener('slideshow:select', this.#handleSelect);
    this.#gallery?.removeEventListener('pointerdown', this.#handleDown);
    this.#gallery?.removeEventListener('pointermove', this.#handleMove);
    this.#gallery?.removeEventListener('pointerup', this.#handleUp);
    this.#gallery?.removeEventListener('pointercancel', this.#handleUp);
    this.#gallery?.removeEventListener('dragstart', this.#handleDragStart);
    this.#gallery?.removeEventListener('click', this.#handleClick, { capture: true });
  }

  #secondImage() {
    const picker = this.#card?.querySelector('swatches-variant-picker-component');
    if (picker instanceof HTMLElement && picker.dataset.productId !== this.#card?.dataset.productId) return null;
    const slides = this.#gallery?.querySelectorAll('slideshow-slide:not([hidden])') ?? [];
    return slides[1]?.querySelector('img') ?? null;
  }

  #handleDown = (event) => {
    this.#dragged = false;
    if (!isMobileBreakpoint() || !event.isPrimary || event.button !== 0) return;
    if (this.closest('.resource-list__carousel, product-recommendations .section-resource-list .resource-list--grid')) return;
    if (event.target instanceof Element && event.target.closest('button, .quick-add, eme-countdown')) return;
    const second = this.#secondImage();
    if (!second) return;
    this.#ensureReveal(second);
    this.#start = { x: event.clientX, progress: this.#progress, width: this.#gallery.clientWidth };
    this.#reveal.style.transition = 'none';
    this.#gallery.setPointerCapture(event.pointerId);
  };

  #handleMove = (event) => {
    if (!this.#start) return;
    const dx = event.clientX - this.#start.x;
    if (isDrag(dx)) this.#dragged = true;
    this.#setProgress(dragProgress(this.#start.progress, dx, this.#start.width));
  };

  #handleUp = () => {
    if (!this.#start) return;
    this.#start = null;
    this.#reveal.style.transition = '';
    this.#setProgress(snapProgress(this.#progress));
    setTimeout(() => {
      this.#dragged = false;
    });
  };

  #handleDragStart = (event) => {
    if (this.#start) event.preventDefault();
  };

  #handleFocusIn = (event) => {
    if (!isMobileBreakpoint()) return;
    if (!(event.target instanceof Element) || !event.target.matches(':focus-visible')) return;
    const item = this.closest('product-recommendations .section-resource-list .resource-list--grid > .resource-list__item');
    item?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  };

  #handleVariantSelect = () => {
    this.#resetReveal();
  };

  #handleClick = (event) => {
    if (!this.#dragged) return;
    this.#dragged = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #ensureReveal(second) {
    if (this.#reveal) return;
    const layer = second.cloneNode();
    layer.className = 'eme-card-image-fade__image eme-card-image-fade__reveal';
    layer.alt = '';
    layer.removeAttribute('loading');
    const { objectFit, objectPosition } = getComputedStyle(second);
    layer.style.objectFit = objectFit;
    layer.style.objectPosition = objectPosition;
    layer.style.clipPath = revealClip(0);
    this.append(layer);
    this.#reveal = layer;
  }

  #setProgress(progress) {
    this.#progress = progress;
    if (this.#reveal) this.#reveal.style.clipPath = revealClip(progress);
  }

  #resetReveal() {
    this.#progress = 0;
    this.#reveal?.remove();
    this.#reveal = null;
  }

  #handleSelect = (event) => {
    this.#resetReveal();
    const { index, previousIndex, slide } = event.detail ?? {};
    const shown = this.#gallery?.contains(this.#shownSlide) ? this.#shownSlide : null;
    if (slide) this.#shownSlide = slide;
    if (prefersReducedMotion() || index === previousIndex || !slide) return;

    const outgoing = (shown ?? event.target.slides?.[previousIndex])?.querySelector('img');
    const incoming = slide.querySelector('img');
    if (!outgoing || !incoming || outgoing === incoming) return;

    this.#fadeOut(outgoing);
    incoming.animate([{ opacity: 0, transform: ZOOMED }, { opacity: 1, transform: RESTING }], TIMING);
  };

  async #fadeOut(image) {
    const layer = image.cloneNode();
    const { objectFit, objectPosition } = getComputedStyle(image);
    layer.className = 'eme-card-image-fade__image';
    layer.alt = '';
    layer.removeAttribute('loading');
    layer.style.objectFit = objectFit;
    layer.style.objectPosition = objectPosition;
    this.replaceChildren(layer);

    const animation = layer.animate([{ opacity: 1, transform: RESTING }, { opacity: 0, transform: ZOOMED }], {
      ...TIMING,
      fill: 'forwards',
    });

    try {
      await animation.finished;
    } catch {
      return;
    }
    layer.remove();
  }
}

if (!customElements.get('eme-card-image-fade')) {
  customElements.define('eme-card-image-fade', EmeCardImageFade);
}
